import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import panelApi from "@/lib/panelApi";
import { apiError } from "@/lib/api";
import { useHolidays, HOLIDAYS_KEY } from "@/lib/useHolidays";
import { useBase } from "@/context/BaseContext";
import { useAuth } from "@/context/AuthContext";
import { isGestor } from "@/lib/permissions";
import { Loading, Empty } from "@/components/DataState";
import { fmtDate } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Pencil, Trash2, Plus, X, CalendarHeart } from "lucide-react";

const EMPTY = { date: "", name: "", type: "feriado", base_id: "" };
const TYPE_LABEL = { feriado: "Feriado", sem_expediente: "Sem expediente" };
const selCls = "h-9 rounded-md border border-slate-700 bg-slate-950 px-2 text-sm text-slate-100";

function HolidayForm({ initial, bases, onDone }) {
  const [f, setF] = useState(initial);
  const [busy, setBusy] = useState(false);
  const qc = useQueryClient();
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));
  const save = async () => {
    if (!f.date || !f.name.trim()) { toast.error("Informe data e nome"); return; }
    setBusy(true);
    try {
      const body = { ...f, base_id: f.base_id || null };
      if (f.id) await panelApi.put(`/panel/holidays/${f.id}`, body);
      else await panelApi.post("/panel/holidays", body);
      toast.success(f.id ? "Data atualizada" : "Data cadastrada");
      await qc.invalidateQueries({ queryKey: HOLIDAYS_KEY });
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div className="flex flex-wrap items-end gap-2 rounded-xl border border-orange-500/30 bg-slate-900 p-3" data-testid="holiday-form">
      <Input type="date" value={f.date} onChange={set("date")} data-testid="holiday-date-input" className="h-9 w-40 border-slate-700 bg-slate-950 text-slate-100" />
      <Input value={f.name} onChange={set("name")} placeholder="Ex: Feriado Nacional" data-testid="holiday-name-input" className="h-9 min-w-[200px] flex-1 border-slate-700 bg-slate-950 text-slate-100" />
      <select value={f.type} onChange={set("type")} data-testid="holiday-type-select" className={selCls}>
        <option value="feriado">Feriado</option>
        <option value="sem_expediente">Dia sem expediente</option>
      </select>
      <select value={f.base_id || ""} onChange={set("base_id")} data-testid="holiday-base-select" className={selCls}>
        <option value="">Todas as bases</option>
        {bases.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
      </select>
      <Button size="sm" className="h-9 bg-orange-500 text-white hover:bg-orange-600" disabled={busy} onClick={save} data-testid="holiday-save-btn">Salvar</Button>
      <Button size="sm" variant="ghost" className="h-9 text-slate-400" onClick={onDone} data-testid="holiday-cancel-btn"><X className="h-4 w-4" /></Button>
    </div>
  );
}

export function HolidaysManager() {
  const { data, isLoading } = useHolidays();
  const { bases } = useBase();
  const { user } = useAuth();
  const gestor = isGestor(user);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const baseName = (id) => (id ? bases.find((b) => b.id === id)?.name || "Base removida" : "Todas as bases");
  const remove = async (h) => {
    if (!window.confirm(`Excluir "${h.name}" (${fmtDate(h.date)})?`)) return;
    try {
      await panelApi.delete(`/panel/holidays/${h.id}`);
      toast.success("Data excluída");
      qc.invalidateQueries({ queryKey: HOLIDAYS_KEY });
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div className="space-y-4" data-testid="holidays-manager">
      <div className="flex flex-wrap items-center gap-3">
        <p className="flex items-center gap-2 text-xs text-slate-500">
          <CalendarHeart className="h-3.5 w-3.5" />
          Nessas datas, quem não bateu ponto não aparece em Pendências de ponto.
        </p>
        {gestor && !editing && (
          <Button size="sm" className="ml-auto h-8 bg-orange-500 text-white hover:bg-orange-600" onClick={() => setEditing(EMPTY)} data-testid="holiday-add-btn">
            <Plus className="h-4 w-4" /> Nova data
          </Button>
        )}
      </div>
      {editing && <HolidayForm key={editing.id || "new"} initial={editing} bases={bases} onDone={() => setEditing(null)} />}
      {isLoading ? <Loading /> : (
        <div className="overflow-x-auto rounded-xl border border-slate-800 bg-slate-900">
          <table className="w-full text-sm" data-testid="holidays-table">
            <thead>
              <tr className="border-b border-slate-800 text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">Data</th>
                <th className="px-4 py-3 font-semibold">Nome</th>
                <th className="px-4 py-3 font-semibold">Tipo</th>
                <th className="px-4 py-3 font-semibold">Base</th>
                {gestor && <th className="px-4 py-3 text-right font-semibold">Ações</th>}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800">
              {!(data || []).length && <tr><td colSpan={5}><Empty label="Nenhuma data cadastrada" /></td></tr>}
              {(data || []).map((h) => (
                <tr key={h.id} className="hover:bg-slate-800/40" data-testid={`holiday-row-${h.id}`}>
                  <td className="px-4 py-3 font-mono text-slate-300">{fmtDate(h.date)}</td>
                  <td className="px-4 py-3 text-slate-100">{h.name}</td>
                  <td className="px-4 py-3">
                    <span className={`rounded px-2 py-0.5 text-xs font-semibold ${h.type === "feriado" ? "bg-orange-500/15 text-orange-300" : "bg-sky-500/15 text-sky-300"}`}>{TYPE_LABEL[h.type]}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-400">{baseName(h.base_id)}</td>
                  {gestor && (
                    <td className="px-4 py-3">
                      <div className="flex justify-end gap-1.5">
                        <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300" onClick={() => setEditing({ ...h, base_id: h.base_id || "" })} data-testid={`holiday-edit-${h.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" className="h-8 border-slate-700 px-2 text-slate-300 hover:text-rose-300" onClick={() => remove(h)} data-testid={`holiday-delete-${h.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
